import { Locale } from "@shared/types";
import { PDFFont, PDFPage, rgb } from "pdf-lib";
import { Dimensions, calculateColumnDimensions, getScaledFontSizeForSmallPaper } from "./dimensionUtils";
import { getFixedT } from "./localeUtils";
import { TextAlignment, drawTextLine } from "./textUtils";

export type TableColumnHeader = {
  title: string;
  vendorTitle?: string;
  recipientTitle?: string;
  position: number;
  width: number;
  align: "center" | "left" | "right";
};

export const getLocalizedTableColumns = (dimensions: Dimensions, vendorLocale: Locale, recipientLocale?: Locale): TableColumnHeader[] => {
  const tVendor = getFixedT(vendorLocale);
  const tRecipient = recipientLocale && recipientLocale !== vendorLocale ? getFixedT(recipientLocale) : undefined;

  const createLabel = (key: string, fallback: string) => {
    const vendorTitle = tVendor(key, { defaultValue: fallback });
    if (tRecipient) {
      const recipientTitle = tRecipient(key, { defaultValue: fallback });
      return {
        title: recipientTitle, // Use recipient title as primary (clean, no separator)
        vendorTitle,
        recipientTitle,
      };
    }
    return {
      title: vendorTitle,
      vendorTitle,
      recipientTitle: vendorTitle,
    };
  };

  // Calculate column widths and positions using shared helper
  const { qtyWidth, itemWidth, descWidth, weightWidth, grindWidth, qtyPosition, itemPosition, descPosition, grindPosition } =
    calculateColumnDimensions(dimensions);

  return [
    {
      ...createLabel("components.packingSlip.columns.qty", "QTY"),
      position: qtyPosition,
      width: qtyWidth,
      align: "center" as const,
    },
    {
      ...createLabel("components.packingSlip.columns.item", "ITEM #"),
      position: itemPosition,
      width: itemWidth,
      align: "left" as const,
    },
    {
      ...createLabel("components.packingSlip.columns.description", "DESCRIPTION"),
      position: descPosition,
      width: descWidth,
      align: "left" as const,
    },
    {
      ...createLabel("components.packingSlip.columns.weight", "WEIGHT"),
      position: grindPosition - weightWidth - 5, // 5px gap between weight and grind
      width: weightWidth,
      align: "center" as const,
    },
    {
      ...createLabel("components.packingSlip.columns.grind", "GRIND"),
      position: grindPosition,
      width: grindWidth,
      align: "center" as const,
    },
  ];
};

// Helper function to check if two-line headers are needed
export const needsTwoLineHeaders = (
  hasTwoLanguages: boolean,
  dimensions: Dimensions,
  vendorLocale: Locale,
  recipientLocale?: Locale
): boolean => {
  if (!hasTwoLanguages) {
    return false;
  }

  const columns = getLocalizedTableColumns(dimensions, vendorLocale, recipientLocale);
  return columns.some((column) => column.vendorTitle !== column.recipientTitle);
};

// Helper function to draw table border
export const drawTableBorder = (page: PDFPage, dimensions: Dimensions, borderTopY: number, borderBottomY: number): void => {
  const borderColor = rgb(0, 0, 0);
  const borderThickness = 1;

  // Top border
  page.drawLine({
    start: { x: dimensions.margin, y: dimensions.height - borderTopY },
    end: { x: dimensions.width - dimensions.margin, y: dimensions.height - borderTopY },
    thickness: borderThickness,
    color: borderColor,
  });

  // Right border
  page.drawLine({
    start: { x: dimensions.width - dimensions.margin, y: dimensions.height - borderTopY },
    end: { x: dimensions.width - dimensions.margin, y: dimensions.height - borderBottomY },
    thickness: borderThickness,
    color: borderColor,
  });

  // Bottom border
  page.drawLine({
    start: { x: dimensions.width - dimensions.margin, y: dimensions.height - borderBottomY },
    end: { x: dimensions.margin, y: dimensions.height - borderBottomY },
    thickness: borderThickness,
    color: borderColor,
  });

  // Left border
  page.drawLine({
    start: { x: dimensions.margin, y: dimensions.height - borderBottomY },
    end: { x: dimensions.margin, y: dimensions.height - borderTopY },
    thickness: borderThickness,
    color: borderColor,
  });
};

export interface TableHeaderColumn {
  position: number;
  width?: number;
  align: TextAlignment;
  title: string;
  vendorTitle?: string;
  recipientTitle?: string;
}

// Helper function to calculate text X position based on alignment
const calculateHeaderTextX = (column: TableHeaderColumn, textWidth: number): number => {
  if (column.align === "center") {
    return column.position + ((column.width ?? 0) - textWidth) / 2;
  }
  if (column.align === "right") {
    return column.position + (column.width ?? 0) - textWidth;
  }
  return column.position;
};

// Helper function to draw text at specific position - uses existing drawTextLine function
const drawHeaderText = (page: PDFPage, text: string, x: number, y: number, font: PDFFont, fontSize: number): void => {
  drawTextLine(page, text, x, y, fontSize, font);
};

// Helper function to draw single language header
const drawSingleLanguageHeader = (
  page: PDFPage,
  column: TableHeaderColumn,
  title: string,
  fontSize: number,
  boldFont: PDFFont,
  tableStartY: number,
  dimensions: Dimensions
): void => {
  const titleWidth = boldFont.widthOfTextAtSize(title, fontSize);
  const textX = calculateHeaderTextX(column, titleWidth);
  const textY = dimensions.height - (tableStartY + 3.5);

  drawHeaderText(page, title, textX, textY, boldFont, fontSize);
};

export interface DrawTwoLanguageHeaderOptions {
  page: PDFPage;
  column: TableHeaderColumn;
  title: string;
  fontSize: number;
  font: PDFFont;
  boldFont: PDFFont;
  tableStartY: number;
  dimensions: Dimensions;
}

// Helper function to draw two-language header
const drawTwoLanguageHeader = (options: DrawTwoLanguageHeaderOptions): void => {
  const { page, column, title, fontSize, font, boldFont, tableStartY, dimensions } = options;
  const vendorTitle = column.vendorTitle ?? title;
  const recipientTitle = column.recipientTitle ?? title;

  // Draw recipient title (first line, bold)
  const recipientTitleWidth = boldFont.widthOfTextAtSize(recipientTitle, fontSize);
  const recipientTextX = calculateHeaderTextX(column, recipientTitleWidth);
  const recipientTextY = dimensions.height - (tableStartY + 3.5);

  drawHeaderText(page, recipientTitle, recipientTextX, recipientTextY, boldFont, fontSize);

  // Only draw vendor title (second line) if it differs from recipient title
  if (vendorTitle !== recipientTitle) {
    const vendorTitleWidth = font.widthOfTextAtSize(vendorTitle, fontSize);
    const vendorTextX = calculateHeaderTextX(column, vendorTitleWidth);
    const vendorTextY = dimensions.height - (tableStartY + 3.5 + 8); // 8px below first line

    drawHeaderText(page, vendorTitle, vendorTextX, vendorTextY, font, fontSize);
  }
};

export interface TableHeaderOptions {
  dimensions: Dimensions;
  startY: number;
  font: PDFFont;
  boldFont: PDFFont;
  vendorLocale: Locale;
  recipientLocale?: Locale;
}

export const drawTableHeader = (page: PDFPage, options: TableHeaderOptions): number => {
  const { dimensions, startY, font, boldFont, vendorLocale, recipientLocale } = options;
  const hasTwoLanguages = recipientLocale && recipientLocale !== vendorLocale;
  const tableStartY = startY + 10;

  // Check if we actually need two lines by examining column titles
  const needsTwoLines = needsTwoLineHeaders(!!hasTwoLanguages, dimensions, vendorLocale, recipientLocale ?? vendorLocale);
  const tableLineHeight = needsTwoLines ? 20 : 13; // Adjust height based on actual need

  // Draw table border
  const headerTextY = tableStartY + 3.5;
  const borderTopY = headerTextY - 9;
  const borderBottomY = borderTopY + tableLineHeight;
  drawTableBorder(page, dimensions, borderTopY, borderBottomY);

  // Draw column headers (localized)
  const columns = getLocalizedTableColumns(dimensions, vendorLocale, recipientLocale);
  for (const column of columns) {
    const fontSize = getScaledFontSizeForSmallPaper(7, 6, dimensions); // Smaller font for 3x4
    const title = column.title;

    if (needsTwoLines) {
      drawTwoLanguageHeader({ page, column, title, fontSize, font, boldFont, tableStartY, dimensions });
    } else {
      drawSingleLanguageHeader(page, column, title, fontSize, boldFont, tableStartY, dimensions);
    }
  }

  return tableStartY + tableLineHeight + 5;
};
